/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import React from "react"
import { Touchable } from "frontend/src/components/Touchable"
import { useCart } from "frontend/src/utils/useCart"
import { useState } from "react"
import { Icon } from "frontend/src/components/Icon"
import { Flex, SectionGrid, Grid } from "../../components/Layout"
import { ProductVariantCard, VariantCard } from "./ProductVariantCard"
import Carousel from "nuka-carousel"
import { Carousel as RCarousel } from "react-responsive-carousel"
import "react-responsive-carousel/lib/styles/carousel.min.css"
import { CMSLink } from "../../components/CMSLink"
import { BlockText } from "frontend/src/components/BlockText"
import { useQueryParam, StringParam } from "use-query-params"
import "./RCarousel.css"
import Img from "gatsby-image"
import { getFluidFromAsset } from "frontend/src/utils/gatsbyImageHelpers"

const generalStyles = {
  textAlign: "center",
  fontFamily: "heading",
  width: ["100%", "unset"],
  fontSize: "16px",
  lineHeight: 1.0,
  fontWeight: "light",
  textTransform: "uppercase",
  px: 7,
  py: 5,
  opacity: 1,
  cursor: "pointer",
  transition: "all 0.1s linear",
  ":focus": { outline: "none", opacity: "0.9" },
  ":active": { opacity: "0.8" },
  ":hover": { opacity: 0.6 },
}
const buttonStyles = {
  ...generalStyles,
  backgroundColor: "almostBlack",
  color: "background",
  border: "none",
}

// show correct thumbnails
const MatchImage = children => {
  return children
}

function ReviewStars({ stars }) {
  return new Array(5)
    .fill("")
    .map((_, i) => (
      <Icon
        color={i < stars ? "stage" : "rgba(35,42,65,0.125)"}
        name="StarBlack"
        size={14}
        key={`star-${i}`}
        sx={{ mr: 3 }}
      />
    ))
}

function ProductBenefits({ benefits }) {
  return (
    <p sx={{ fontSize: 0, fontWeight: 500, fontFamily: "heading" }}>
      {benefits &&
        benefits.map((benefit, index) => {
          const iconSize = benefit.icon === "Lieferzeit" ? 18 : 16
          return (
            <span key={index} sx={{ display: "inline-flex", alignItems: "center", mr: 3 }}>
              <Icon sx={{ mr: 2 }} name={benefit.icon} size={iconSize} />
              {benefit.text}
            </span>
          )
        })}
    </p>
  )
}

function SectionProduct({ title, products, toggleBrandName, titleVariantSelector }) {
  const cart = useCart()

  const [variant] = useQueryParam("variant", StringParam)
  const viewPort = typeof window !== `undefined` ? window.innerWidth : null
  const variants = products.length > 1 ? products : products[0].variants
  const variantSKUs = products.reduce((acc, curr) => {
    const { variants } = curr
    const visibleVariants = variants.filter(variant => variant.hideVariant === false)
    const variantIDs = visibleVariants.map(variant => variant.ourID)
    const arr = acc.concat(variantIDs)
    return arr
  }, [])
  const presetVariant = variant ? variantSKUs.indexOf(variant) : -1
  const initialActiveVariant = presetVariant !== -1 ? presetVariant : variants.length > 2 ? 1 : 0

  const [activeVariant, setActiveVariant] = useState(initialActiveVariant)
  const [collapseActive, setCollapseActive] = useState(viewPort >= 999)
  const [emailValue, setEmailValue] = useState(null)

  const toggleCollapse = () => setCollapseActive(!collapseActive)
  const pureName = title ? title : products[0].name
  const productName = toggleBrandName ? `sleep.ink ${pureName}` : pureName

  const reviewCount = products.reduce(
    (intermediateSum, variant) => intermediateSum + variant.reviewCount,
    0
  )

  const reviewRatingSum = products.reduce(
    (intermediateSum, variant) => intermediateSum + variant.reviewCount * variant.reviewRating,
    0
  )
  const reviewRating = reviewCount > 0 ? reviewRatingSum / reviewCount : 0
  const reviewsLink = products[0].reviewsLink

  const getDelayMessage = object => {
    if (object.delayMode === "none") return <span>Auf Lager &ndash; in 1-2 Tagen bei Ihnen</span>
    if (object.delayMode === "text")
      return <span sx={{ color: "accent" }}>{object.delayString}</span>
    return <span>Auf Lager &ndash; in 1-2 Tagen bei Ihnen</span>
  }

  if (variants.length === 1) {
    return (
      <SectionGrid sx={{ bg: "gray_100", pt: 8, pb: 9 }} id="ProductSection">
        <SectionGrid.wide sx={{ px: 6, mx: -6 }}>
          <Grid columns="100px 1fr 500px" sx={{ width: "100%", display: ["none", "none", "grid"] }}>
            <div sx={{ gridColumn: "1 / span 2", width: "750px", height: "600px" }}>
              <RCarousel
                autoPlay={false}
                infiniteLoop={true}
                showStatus={false}
                enableKeyboardControls={true}
                dynamicHeight={true}
                swipeable={true}
                emulateTouch={true}
                showIndicators={false}
                renderThumbs={MatchImage}
                sx={{ mx: "auto" }}
              >
                {products[0].secondaryImages.map(image => {
                  return (
                    <Img
                      fluid={getFluidFromAsset(image, 2000)}
                      sx={{
                        display: "inline-block",
                        width: "600px",
                        height: "600px",
                      }}
                      imgStyle={{ objectFit: "contain" }}
                    />
                  )
                })}
              </RCarousel>
            </div>
            <Flex.column
              justify="center"
              align="center"
              sx={{ gridColumn: "3 / span 1", textAlign: "center", pb: 0, mb: 4 }}
            >
              <div>
                <h2
                  sx={{
                    variant: "text.h2",
                    letterSpacing: ["0.4px", "0.21px", "0.34px", "0.56px"],
                    pb: 0,
                    mb: 4,
                  }}
                >
                  {productName}
                </h2>
                <CMSLink target="false" link={reviewsLink} sx={{ fontSize: 1 }}>
                  <ReviewStars stars={reviewRating} />
                  <span sx={{ ml: 2, fontWeight: 500, fontFamily: "heading" }}>
                    ({reviewCount})
                  </span>
                </CMSLink>
              </div>
              <div
                key={`variant-${variants[0].ourID}`}
                sx={{ my: 7, px: [3, 3, 3, 5], width: "100%" }}
              >
                <VariantCard
                  name={products[0].name}
                  onClick={() => setActiveVariant(0)}
                  active={activeVariant === 0}
                  index={0}
                  variant={variants[0]}
                />
              </div>
              {products[0].variants[0].inStock ? (
                <div>
                  <Touchable
                    onClick={() => {
                      cart.addSingle({
                        productID: products[0].ourID,
                        variantID: products[0].variants[0].ourID,
                      })
                      cart.show()
                    }}
                    sx={{
                      variant: "text.h3",
                      textTransform: "uppercase",
                      fontSize: [3, 3, 4, 5],
                      color: "white",
                      py: 5,
                      px: 11,
                      bg: "almostBlack",
                      mt: 8,
                      mb: 4,
                    }}
                  >
                    In den Warenkorb
                  </Touchable>
                  <div
                    sx={{
                      fontFamily: "body",
                      fontWeight: "medium",
                      fontSize: 0,
                      lineHeight: 1.5,
                      height: "1.5em",
                      textAlign: "center",
                      color: "#007600",
                    }}
                  >
                    {variants[0].available ? (
                      variants[0].delayMode === "none" ? (
                        <React.Fragment>
                          <Icon name="Dot" size="1em" color="#007600" sx={{ paddingTop: "5px" }} />{" "}
                          {getDelayMessage(variants[0])}
                        </React.Fragment>
                      ) : (
                        <React.Fragment>{getDelayMessage(variants[0])}</React.Fragment>
                      )
                    ) : (
                      <span>&nbsp;</span>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  sx={{
                    width: "100%",
                  }}
                >
                  <p
                    sx={{
                      variant: "body2",
                      fontSize: 1,
                      mb: 4,
                      color: "accent",
                    }}
                  >
                    Es tut uns leid, dieses Produkt ist aktuell ausverkauft. Wenn Sie sich in die
                    Liste eintragen, benachrichtigen wir Sie per E-Mail, sobald das Produkt wieder
                    verfügbar ist.
                  </p>
                  <form
                    id="waitlist_signup"
                    action="https://hook.integromat.com/ldw7yi6fsxww8dklemxuejiphcaz5ddq"
                    method="post"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: ["space-around", "center"],
                      alignItems: ["center", "cemter", "center"],
                      width: "100%",
                    }}
                  >
                    <input type="hidden" name="g" value={products[0].variants[0].listName} />
                    <input type="hidden" name="$fields" value="$source" />
                    <input type="hidden" name="$source" value="out-of-stock" />
                    <input
                      type="email"
                      name="email"
                      placeholder="E-Mail-Adresse"
                      onChange={e => setEmailValue(e.target.value)}
                      required
                      value={emailValue}
                      id="checkNow"
                      sx={{
                        width: ["100%"],
                        maxWidth: ["unset", "450px", "370px"],
                        height: "50px",
                        backgroundColor: "#ffffff",
                        border: "1px solid #999999",
                        borderRadius: "5px",
                        mb: [3],
                        padding: 5,
                        fontSize: 0,
                        fontFamily: "body",
                        lineHeight: 1.42857143,
                        color: "#333333",
                        verticalAlign: "middle",
                        "&:active": {
                          border: "none",
                          outline: "none",
                        },
                        "&:focus": {
                          border: "none",
                          outline: "none",
                        },
                      }}
                    />
                    <button
                      type="submit"
                      sx={{
                        ...buttonStyles,
                      }}
                    >
                      Ich möchte benachrichtigt werden
                    </button>
                  </form>
                </div>
              )}
            </Flex.column>
          </Grid>
          <div sx={{ width: "95%", mx: "auto", display: ["block", "block", "none"] }}>
            <div sx={{ textAlign: "center", mb: 8 }}>
              <h2
                sx={{
                  variant: "text.h2",
                  letterSpacing: ["0.4px", "0.21px", "0.34px", "0.56px"],
                  pb: 0,
                  mb: 4,
                }}
              >
                {productName}
              </h2>
              <CMSLink target="false" link={reviewsLink} sx={{ fontSize: 1 }}>
                <ReviewStars stars={reviewRating} />
                <span sx={{ ml: 2, fontWeight: 500, fontFamily: "heading" }}>({reviewCount})</span>
              </CMSLink>
            </div>
            <div sx={{ width: "100%" }}>
              <RCarousel
                autoPlay={false}
                infiniteLoop={true}
                showStatus={false}
                enableKeyboardControls={true}
                dynamicHeight={true}
                swipeable={true}
                emulateTouch={true}
                showIndicators={false}
                renderThumbs={MatchImage}
                sx={{ width: "350px", mx: "auto" }}
              >
                {products[0].secondaryImages.map(image => {
                  return (
                    <Img
                      fluid={getFluidFromAsset(image, 1500)}
                      sx={{
                        display: "inline-block",
                        width: "350px",
                        height: "350px",
                      }}
                      imgStyle={{ objectFit: "contain" }}
                    />
                  )
                })}
              </RCarousel>
            </div>
            <Flex.column
              justify="center"
              align="center"
              sx={{ gridColumn: "3 / span 1", textAlign: "center", pb: 0, mb: 4 }}
            >
              <div
                key={`variant-${variants[0].ourID}`}
                sx={{ my: 6, px: [3, 3, 3, 5], width: "100%" }}
              >
                <VariantCard
                  name={products[0].name}
                  onClick={() => setActiveVariant(0)}
                  active={activeVariant === 0}
                  index={0}
                  variant={variants[0]}
                />
              </div>
              {products[0].variants[0].inStock ? (
                <div>
                  <Touchable
                    onClick={() => {
                      cart.addSingle({
                        productID: products[0].ourID,
                        variantID: products[0].variants[0].ourID,
                      })
                      cart.show()
                    }}
                    sx={{
                      variant: "text.h3",
                      textTransform: "uppercase",
                      fontSize: [3, 3, 4, 5],
                      color: "white",
                      py: 5,
                      px: 11,
                      bg: "almostBlack",
                      mt: 8,
                      mb: 4,
                    }}
                  >
                    In den Warenkorb
                  </Touchable>
                  <div
                    sx={{
                      fontFamily: "body",
                      fontWeight: "medium",
                      fontSize: 0,
                      lineHeight: 1.5,
                      height: "1.5em",
                      textAlign: "center",
                      color: "#007600",
                    }}
                  >
                    {variants[0].available ? (
                      variants[0].delayMode === "none" ? (
                        <React.Fragment>
                          <Icon name="Dot" size="1em" color="#007600" />{" "}
                          {getDelayMessage(variants[0])}
                        </React.Fragment>
                      ) : (
                        <React.Fragment>{getDelayMessage(variants[0])}</React.Fragment>
                      )
                    ) : (
                      <span>&nbsp;</span>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  sx={{
                    width: "100%",
                  }}
                >
                  <p
                    sx={{
                      variant: "body2",
                      fontSize: 1,
                      mb: 4,
                      color: "accent",
                    }}
                  >
                    Es tut uns leid, dieses Produkt ist aktuell ausverkauft. Wenn Sie sich in die
                    Liste eintragen, benachrichtigen wir Sie per E-Mail, sobald das Produkt wieder
                    verfügbar ist.
                  </p>
                  <form
                    id="waitlist_signup"
                    action="https://hook.integromat.com/ldw7yi6fsxww8dklemxuejiphcaz5ddq"
                    method="post"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: ["space-around", "center"],
                      alignItems: ["center", "cemter", "center"],
                      width: "100%",
                    }}
                  >
                    <input type="hidden" name="g" value={products[0].variants[0].listName} />
                    <input type="hidden" name="$fields" value="$source" />
                    <input type="hidden" name="$source" value="out-of-stock" />
                    <input
                      type="email"
                      name="email"
                      placeholder="E-Mail-Adresse"
                      onChange={e => setEmailValue(e.target.value)}
                      required
                      value={emailValue}
                      id="checkNow"
                      sx={{
                        width: ["100%"],
                        maxWidth: ["unset", "450px", "370px"],
                        height: "50px",
                        backgroundColor: "#ffffff",
                        border: "1px solid #999999",
                        borderRadius: "5px",
                        mb: [3],
                        padding: 5,
                        fontSize: 0,
                        fontFamily: "body",
                        lineHeight: 1.42857143,
                        color: "#333333",
                        verticalAlign: "middle",
                        "&:active": {
                          border: "none",
                          outline: "none",
                        },
                        "&:focus": {
                          border: "none",
                          outline: "none",
                        },
                      }}
                    />
                    <button
                      type="submit"
                      sx={{
                        ...buttonStyles,
                      }}
                    >
                      Ich möchte benachrichtigt werden
                    </button>
                  </form>
                </div>
              )}
            </Flex.column>
          </div>
        </SectionGrid.wide>
        <SectionGrid.narrow sx={{ px: 6, mx: -6 }}>
          <div
            sx={{
              bg: "gray_100",
              pt: 5,
              display: ["none", "none", "block", "block"],
              zIndex: 10,
              textAlign: "center",
            }}
          >
            <h4
              sx={{
                variant: "text.h4",
                textTransform: "uppercase",
                letterSpacing: ["0.4px", "0.21px", "0.34px", "0.56px"],
              }}
            >
              Beschreibung
            </h4>
            <BlockText
              style={{ fontSize: 0, fontWeight: "regular", mt: 3, mb: 7 }}
              blocks={products[0].longDescription}
            />
            <ProductBenefits benefits={products[0].benefits} />
          </div>
        </SectionGrid.narrow>
        <SectionGrid.narrow
          sx={{
            display: ["block", "block", "none", "none"],
            px: 9,
            py: 7,
            mx: [-6, -6, 0],
            mb: 9,
            borderTopWidth: "1px",
            borderTopStyle: "solid",
            borderTopColor: ["almostBlack", "almostBlack", "rgba(17,17,17,0.08)"],
            borderBottomWidth: "1px",
            borderBottomStyle: "solid",
            borderBottomColor: ["almostBlack", "almostBlack", "rgba(17,17,17,0.08)"],
          }}
        >
          <Flex.row onClick={toggleCollapse} align="center" justify="space-between">
            <h4 sx={{ variant: "text.h4", textTransform: "uppercase" }}>Beschreibung</h4>
            <span sx={{ variant: "text.h4" }}>{collapseActive ? "-" : "+"}</span>
          </Flex.row>
          <div sx={{ display: collapseActive ? "block" : "none" }}>
            <BlockText
              blocks={products[0].longDescription}
              style={{ fontSize: 0, fontWeight: "normal", mt: 3, mb: 4 }}
            />
            <ProductBenefits benefits={products[0].benefits} />
          </div>
        </SectionGrid.narrow>
      </SectionGrid>
    )
  }

  return (
    <SectionGrid sx={{ bg: "gray_100", pt: 8, pb: 9 }}>
      <SectionGrid.wide sx={{ px: 6, mx: -6 }}>
        <div sx={{ textAlign: "center", pb: 0, mb: 4 }}>
          <h2
            sx={{
              variant: "text.h2",
              letterSpacing: ["0.4px", "0.21px", "0.34px", "0.56px"],
              pb: 0,
              mb: 4,
            }}
          >
            {productName}
          </h2>
          <CMSLink target="false" link={reviewsLink} sx={{ fontSize: 1 }}>
            <ReviewStars stars={reviewRating} />
            <span sx={{ ml: 2, fontWeight: 500, fontFamily: "heading" }}>({reviewCount})</span>
          </CMSLink>
        </div>
      </SectionGrid.wide>
      <SectionGrid.wide>
        <Flex direction="column" align="center" sx={{ mx: [-6, -6, 0], px: 6, mb: 4 }}>
          {titleVariantSelector && titleVariantSelector !== "" ? (
            <h2
              sx={{
                variant: "text.subtitle2",
                fontWeight: 500,
                mb: 8,
                textAlign: "center",
                px: 6,
                letterSpacing: ["0.4px", "0.21px", "0.34px", "0.56px"],
              }}
            >
              {titleVariantSelector}
            </h2>
          ) : (
            <div sx={{ height: "25px" }} />
          )}
          <Flex
            direction="row"
            align="stretch"
            justify="center"
            sx={{ width: "100%", display: ["none", "none", "flex"] }}
          >
            {variants.map((variant, i) => {
              if (variant.hasOwnProperty("hideVariant") && variant.hideVariant === false) {
                return (
                  <div key={`variant-${variant.ourID}`} sx={{ px: [3, 3, 3, 5], width: "33%" }}>
                    <ProductVariantCard
                      onClick={() => setActiveVariant(i)}
                      active={activeVariant === i}
                      index={i}
                      variant={variant}
                    />
                  </div>
                )
              } else if (
                variant.hasOwnProperty("variants") &&
                variant.variants[0].hideVariant === false
              ) {
                return (
                  <div
                    key={`variant-${variant.variants[0].ourID}`}
                    sx={{ px: [3, 3, 3, 5], width: "33%" }}
                  >
                    <ProductVariantCard
                      onClick={() => setActiveVariant(i)}
                      active={activeVariant === i}
                      index={i}
                      variant={variant.variants[0]}
                    />
                  </div>
                )
              } else {
                return null
              }
            })}
          </Flex>
          <div sx={{ display: ["flex", "flex", "none"], width: "100%" }}>
            <Carousel
              slideIndex={activeVariant}
              afterSlide={slideIndex => setActiveVariant(slideIndex)}
              cellAlign="center"
              cellSpacing={10}
              enableKeyboardControls={true}
              opacityScale={0.4}
              slideWidth={"232px"}
              renderCenterLeftControls={() => <button sx={{ display: "none" }}>Previous</button>}
              renderCenterRightControls={() => <button sx={{ display: "none" }}>Next</button>}
              renderBottomCenterControls={() => <button sx={{ display: "none" }}>Next</button>}
            >
              {variants.map((variant, i) => {
                if (variant.hasOwnProperty("hideVariant") && variant.hideVariant === false) {
                  return (
                    <div key={`slide-${variant.ourID}`} sx={{ py: 6 }}>
                      <ProductVariantCard
                        cardStyles={{
                          "&:hover": {
                            opacity: 1,
                          },
                        }}
                        onClick={() => setActiveVariant(i)}
                        active={activeVariant === i}
                        index={i}
                        variant={variant}
                      />
                    </div>
                  )
                } else if (
                  variant.hasOwnProperty("variants") &&
                  variant.variants[0].hideVariant === false
                ) {
                  return (
                    <div key={`variant-${variant.variants[0].ourID}`} sx={{ py: 6 }}>
                      <ProductVariantCard
                        onClick={() => setActiveVariant(i)}
                        active={activeVariant === i}
                        index={i}
                        variant={variant.variants[0]}
                      />
                    </div>
                  )
                } else {
                  return null
                }
              })}
            </Carousel>
          </div>
          <Touchable
            onClick={() => {
              cart.addSingle({
                productID:
                  products.length === 1 ? products[0].ourID : variants[activeVariant].ourID,
                variantID:
                  products.length === 1
                    ? products[0].variants[activeVariant].ourID
                    : variants[activeVariant].variants[0].ourID,
              })
              cart.show()
            }}
            sx={{
              variant: "text.h3",
              textTransform: "uppercase",
              fontSize: [3, 3, 4, 5],
              color: "white",
              py: 5,
              px: 11,
              bg: "almostBlack",
              mt: 8,
              mb: 4,
            }}
          >
            In den Warenkorb
          </Touchable>
          <div
            sx={{
              fontFamily: "body",
              fontWeight: "medium",
              fontSize: 0,
              lineHeight: 1.5,
              height: "1.5em",
              textAlign: "center",
              color: "#007600",
            }}
          >
            {products.length === 1 ? (
              variants[activeVariant].available ? (
                variants[activeVariant].delayMode === "none" ? (
                  <React.Fragment>
                    <Icon name="Dot" size="1em" color="#007600" />{" "}
                    {getDelayMessage(variants[activeVariant])}
                  </React.Fragment>
                ) : (
                  <React.Fragment>{getDelayMessage(variants[activeVariant])}</React.Fragment>
                )
              ) : (
                <span>&nbsp;</span>
              )
            ) : variants[activeVariant].variants[0].available ? (
              variants[activeVariant].variants[0].delayMode === "none" ? (
                <React.Fragment>
                  <Icon name="Dot" size="1em" color="#007600" />{" "}
                  {getDelayMessage(variants[activeVariant].variants[0])}
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {getDelayMessage(variants[activeVariant].variants[0])}
                </React.Fragment>
              )
            ) : (
              <span>&nbsp;</span>
            )}
          </div>
        </Flex>
      </SectionGrid.wide>
      <SectionGrid.narrow sx={{ px: 6, mx: -6 }}>
        <div
          sx={{
            bg: "gray_100",
            pt: 5,
            display: ["none", "none", "block", "block"],
            zIndex: 10,
            textAlign: "center",
          }}
        >
          <h4
            sx={{
              variant: "text.h4",
              textTransform: "uppercase",
              letterSpacing: ["0.4px", "0.21px", "0.34px", "0.56px"],
            }}
          >
            Beschreibung
          </h4>
          <BlockText
            style={{ fontSize: 0, fontWeight: "regular", mt: 3, mb: 7 }}
            blocks={products[0].longDescription}
          />
          <ProductBenefits benefits={products[0].benefits} />
        </div>
      </SectionGrid.narrow>
      <SectionGrid.narrow
        sx={{
          display: ["block", "block", "none", "none"],
          px: 9,
          py: 7,
          mx: [-6, -6, 0],
          mb: 9,
          borderTopWidth: "1px",
          borderTopStyle: "solid",
          borderTopColor: ["almostBlack", "almostBlack", "rgba(17,17,17,0.08)"],
          borderBottomWidth: "1px",
          borderBottomStyle: "solid",
          borderBottomColor: ["almostBlack", "almostBlack", "rgba(17,17,17,0.08)"],
        }}
      >
        <Flex.row onClick={toggleCollapse} align="center" justify="space-between">
          <h4 sx={{ variant: "text.h4", textTransform: "uppercase" }}>Beschreibung</h4>
          <span sx={{ variant: "text.h4" }}>{collapseActive ? "-" : "+"}</span>
        </Flex.row>
        <div sx={{ display: collapseActive ? "block" : "none" }}>
          <BlockText
            blocks={products[0].longDescription}
            style={{ fontSize: 0, fontWeight: "normal", mt: 3, mb: 4 }}
          />
          <ProductBenefits benefits={products[0].benefits} />
        </div>
      </SectionGrid.narrow>
    </SectionGrid>
  )
}

export { SectionProduct }
